import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { appAvailabilityQuery, formatMinutes, formatPercent, type AvailabilityDay, type AvailabilityTotals } from '../api/appAvailability'
import './AppAvailability.css'

/**
 * "Availability" pe fisa aplicatiei: cat a fost aplicatia in picioare in
 * ultimele 30 de zile - procentul, minutele jos, numarul de caderi, cea mai
 * lunga cadere - si o bara cu cate o casuta pe zi (verde / galben / rosu /
 * gri = fara date). Ziua de azi e socotita pana acum.
 *
 * Fara dreptul pe aplicatii sau inainte de migrare, cardul nu apare.
 */
export function AppAvailability({ appId }: { appId: number }) {
  const page = useQuery({ ...appAvailabilityQuery(appId, 30), retry: false })
  const data = page.data
  if (!data || data.ready !== true || !Array.isArray(data.days)) return null
  const t = data.totals
  const measured = t.availability !== null

  return (
    <>
      <div className="adm-section">
        <h4>Availability</h4>
        <span className="adm-section-note">last {data.window} days, from the database, application and web servers</span>
      </div>
      <div className="adm-card" id="appAvailability">
        {!measured ? (
          <p className="text-muted" style={{ padding: '10px 15px' }}>
            No monitored database, application or web server yet. Link the servers the application runs on as components
            and their outages will be counted here.
          </p>
        ) : (
          <>
            <div className="av-figures">
              <Figure id="avPercent" label="available" value={<span className={pctClass(t.availability)}>{formatPercent(t.availability)}</span>} />
              <Figure id="avDown" label="down" value={formatMinutes(t.minutesDown)} />
              <Figure id="avOutages" label={t.outages === 1 ? 'outage' : 'outages'} value={String(t.outages)} />
              <Figure id="avLongest" label="longest outage" value={formatMinutes(t.longestDownMinutes)} />
              <Figure id="avDegraded" label="degraded" value={formatMinutes(t.minutesDegraded)} />
            </div>
            <div className="av-bar" aria-label="availability per day">
              {data.days.map((d) => (
                <span key={d.day} className={`av-day av-${d.state}`} title={dayTitle(d)}></span>
              ))}
            </div>
            <div className="av-axis adm-muted small">
              <span>{data.from}</span>
              <span className="av-legend">
                <span className="av-day av-up"></span> up
                <span className="av-day av-degraded"></span> degraded
                <span className="av-day av-down"></span> down
                <span className="av-day av-nodata"></span> no data
              </span>
              <span>{data.to}</span>
            </div>
          </>
        )}
      </div>
    </>
  )
}

/** Celula din lista: procentul pe 30 de zile, colorat. */
export function AvailabilityCell({ totals }: { totals: AvailabilityTotals | null | undefined }) {
  if (!totals) return <span className="adm-muted">-</span>
  if (totals.availability === null) return <span className="adm-muted">no data</span>
  const title = totals.minutesDown > 0
    ? `${formatMinutes(totals.minutesDown)} down in ${totals.outages} outage${totals.outages === 1 ? '' : 's'}`
    : 'no downtime'
  return (
    <span className={pctClass(totals.availability)} title={title}>
      {formatPercent(totals.availability)}
    </span>
  )
}

function Figure({ id, label, value }: { id: string; label: string; value: ReactNode }) {
  return (
    <div id={id}>
      <div className="av-value">{value}</div>
      <div className="adm-muted small">{label}</div>
    </div>
  )
}

function pctClass(value: number | null): string {
  if (value === null) return 'adm-muted'
  if (value >= 99.9) return 'av-pct-good'
  if (value >= 99) return 'av-pct-warn'
  return 'av-pct-bad'
}

function dayTitle(d: AvailabilityDay): string {
  const head = `${d.day}${d.live ? ' (so far)' : ''}: `
  if (d.state === 'nodata') return head + 'no data'
  const parts: string[] = []
  if (d.minutesDown > 0) parts.push(`${formatMinutes(d.minutesDown)} down, ${d.outages} outage${d.outages === 1 ? '' : 's'}`)
  if (d.minutesDegraded > 0) parts.push(`${formatMinutes(d.minutesDegraded)} degraded`)
  if (parts.length === 0) parts.push('up')
  return head + parts.join(', ') + (d.availability !== null && d.availability < 100 ? ` (${formatPercent(d.availability)})` : '')
}
